import { useState, useRef } from 'react';
import { X, Upload, Loader2, FileMusic, Eye, EyeOff, ListMusic } from 'lucide-react';
import {
  uploadLocalFile,
  getAudioDuration,
  addLocalFileToQueue,
  validateLocalAudioFile,
  LocalFileValidationError,
} from '../../services/localFiles';
import toast from 'react-hot-toast';

interface Props {
  eventId: string;
  djId: string;
  onClose: () => void;
  onAdded: () => void;
}

function parseFileName(name: string): { title: string; artist: string } {
  // Remove extension
  const base = name.replace(/\.[^.]+$/, '');
  // Try "Artist - Title" pattern
  const parts = base.split(' - ');
  if (parts.length >= 2) {
    return { artist: parts[0].trim(), title: parts.slice(1).join(' - ').trim() };
  }
  return { title: base.trim(), artist: '' };
}

export default function LocalFileUploadModal({ eventId, djId, onClose, onAdded }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [temporary, setTemporary] = useState(true);
  const [visibleToAttendees, setVisibleToAttendees] = useState(true);
  const [includeInPlaylist, setIncludeInPlaylist] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [durationMs, setDurationMs] = useState(0);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (!selected) return;
    const validationError = validateLocalAudioFile(selected);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setFile(selected);
    const parsed = parseFileName(selected.name);
    setTitle(parsed.title);
    setArtist(parsed.artist);
    try {
      const dur = await getAudioDuration(selected);
      setDurationMs(dur);
    } catch {
      setDurationMs(0);
    }
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      const { storageUrl } = await uploadLocalFile(
        file,
        { eventId, djId, title: title.trim(), artist: artist.trim(), temporary, durationMs },
        (percent) => setUploadProgress(percent)
      );

      await addLocalFileToQueue(eventId, djId, {
        storageUrl,
        fileName: file.name,
        title: title.trim(),
        artist: artist.trim(),
        durationMs,
        temporary,
      }, {
        visibleToAttendees,
        includeInPlaylist,
      });

      toast.success('Local file added to queue');
      onAdded();
      onClose();
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error(err instanceof LocalFileValidationError ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const formatDuration = (ms: number) => {
    if (!ms) return '--:--';
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-xl p-5 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FileMusic className="w-5 h-5 text-primary" />
            Add Local File
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm btn-circle">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* File Picker */}
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/mp3,audio/mpeg,audio/wav,audio/m4a,audio/ogg,audio/x-m4a,.mp3,.wav,.m4a,.ogg"
          className="hidden"
          onChange={handleFileSelect}
        />

        {!file ? (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary/50 transition-colors"
          >
            <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
            <p className="font-medium">Choose audio file</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">MP3, WAV, M4A, OGG</p>
          </button>
        ) : (
          <div className="space-y-3">
            {/* File info */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-2 text-sm">
              <FileMusic className="w-4 h-4 text-primary flex-shrink-0" />
              <span className="truncate">{file.name}</span>
              <span className="text-gray-500 dark:text-gray-400 flex-shrink-0">{formatDuration(durationMs)}</span>
              <button onClick={() => { setFile(null); setTitle(''); setArtist(''); }} className="ml-auto text-gray-400 dark:text-gray-500 hover:text-gray-900 dark:hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Title / Artist */}
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Title</label>
              <input value={title} onChange={e => setTitle(e.target.value)} className="input input-bordered input-sm w-full mt-1" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 dark:text-gray-400">Artist</label>
              <input value={artist} onChange={e => setArtist(e.target.value)} className="input input-bordered input-sm w-full mt-1" placeholder="Optional" />
            </div>

            {/* Storage mode */}
            <div className="flex items-center justify-between">
              <span className="text-sm">Storage</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setTemporary(true)}
                  className={`btn btn-xs ${temporary ? 'btn-primary' : 'btn-ghost'}`}
                >
                  Temporary
                </button>
                <button
                  onClick={() => setTemporary(false)}
                  className={`btn btn-xs ${!temporary ? 'btn-primary' : 'btn-ghost'}`}
                >
                  Permanent
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              {temporary ? 'Auto-deleted when event ends' : 'Saved to your library for reuse'}
            </p>

            {/* Visibility flags */}
            <div className="space-y-2">
              <button
                onClick={() => setVisibleToAttendees(!visibleToAttendees)}
                className="flex items-center gap-2 w-full text-sm"
              >
                {visibleToAttendees ? <Eye className="w-4 h-4 text-primary" /> : <EyeOff className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
                <span>Visible to attendees</span>
                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{visibleToAttendees ? 'ON' : 'OFF'}</span>
              </button>
              <button
                onClick={() => setIncludeInPlaylist(!includeInPlaylist)}
                className="flex items-center gap-2 w-full text-sm"
              >
                {includeInPlaylist ? <ListMusic className="w-4 h-4 text-primary" /> : <ListMusic className="w-4 h-4 text-gray-400 dark:text-gray-500" />}
                <span>Include in post-event playlist</span>
                <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">{includeInPlaylist ? 'ON' : 'OFF'}</span>
              </button>
            </div>

            {/* Upload progress */}
            {uploading && (
              <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleUpload}
                disabled={uploading || !title.trim()}
                className="btn btn-primary flex-1"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadProgress}%
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload & Queue
                  </>
                )}
              </button>
              <button onClick={onClose} disabled={uploading} className="btn btn-ghost">
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
